import { MMKV } from 'react-native-mmkv';
import naclUtil from 'tweetnacl-util';

import type {
  PreparedSupportDiagnostic,
  SupportDiagnosticPreparedStore,
  SupportDiagnosticSnapshot,
} from '@/types/supportDiagnostic';

const STORAGE_ID = 'app-guincho-support-diagnostic';
const LATEST_KEY = 'prepared_support_diagnostic_v1';

interface PersistedPreparedDiagnostic {
  schema: 'app-guincho-support-diagnostic-prepared/v1';
  diagnosticId: string;
  administrativeRequestId?: string;
  createdAt: string;
  byteLength: number;
  sha512: string;
  contentType: 'application/vnd.app-guincho.support-diagnostic+json';
  idempotencyKey: string;
  bodyBase64: string;
}

const parsePrepared = (raw: string): PreparedSupportDiagnostic => {
  const persisted = JSON.parse(raw) as PersistedPreparedDiagnostic;
  if (
    persisted.schema !== 'app-guincho-support-diagnostic-prepared/v1' ||
    !persisted.diagnosticId ||
    !persisted.sha512 ||
    !persisted.idempotencyKey ||
    persisted.contentType !== 'application/vnd.app-guincho.support-diagnostic+json'
  ) {
    throw new Error('Diagnostico persistido invalido.');
  }
  const body = naclUtil.decodeBase64(persisted.bodyBase64);
  if (body.byteLength !== persisted.byteLength) {
    throw new Error('Corpo persistido do diagnostico esta incompleto.');
  }
  const snapshot = JSON.parse(naclUtil.encodeUTF8(body)) as SupportDiagnosticSnapshot;
  if (snapshot.diagnosticId !== persisted.diagnosticId) {
    throw new Error('Identidade do diagnostico persistido diverge.');
  }
  return {
    diagnosticId: persisted.diagnosticId,
    administrativeRequestId: persisted.administrativeRequestId,
    createdAt: persisted.createdAt,
    byteLength: persisted.byteLength,
    sha512: persisted.sha512,
    contentType: persisted.contentType,
    idempotencyKey: persisted.idempotencyKey,
    body,
    snapshot,
  };
};

export class MMKVSupportDiagnosticPreparedStore implements SupportDiagnosticPreparedStore {
  constructor(private readonly storage = new MMKV({ id: STORAGE_ID })) {}

  async save(prepared: PreparedSupportDiagnostic): Promise<void> {
    const persisted: PersistedPreparedDiagnostic = {
      schema: 'app-guincho-support-diagnostic-prepared/v1',
      diagnosticId: prepared.diagnosticId,
      administrativeRequestId: prepared.administrativeRequestId,
      createdAt: prepared.createdAt,
      byteLength: prepared.byteLength,
      sha512: prepared.sha512,
      contentType: prepared.contentType,
      idempotencyKey: prepared.idempotencyKey,
      bodyBase64: naclUtil.encodeBase64(prepared.body),
    };
    this.storage.set(LATEST_KEY, JSON.stringify(persisted));
  }

  async load(diagnosticId: string): Promise<PreparedSupportDiagnostic | null> {
    const prepared = await this.loadLatest();
    return prepared?.diagnosticId === diagnosticId ? prepared : null;
  }

  async loadLatest(): Promise<PreparedSupportDiagnostic | null> {
    const raw = this.storage.getString(LATEST_KEY);
    if (!raw) return null;
    return parsePrepared(raw);
  }

  async remove(diagnosticId: string): Promise<void> {
    const prepared = await this.loadLatest();
    if (prepared?.diagnosticId === diagnosticId) this.storage.delete(LATEST_KEY);
  }

  clear(): void {
    this.storage.delete(LATEST_KEY);
  }
}
